import { createSelector, type PayloadAction } from '@reduxjs/toolkit';

import { createAppSlice } from '../../redux/hooks';
import type { RootState } from '../../redux/store';
import type { Message, Website } from '../../types/api';
import { getAncestorId } from '../Discussion/getAncestor';
import { newResponseKeyInBbf } from './thread-service';
import {
  addMessagesToTree,
  addMessageToTree,
  assertMessageExists,
  deleteMessageChildren,
  getStateMessageStrict,
  openFirstBranch,
} from './thread-slice-utils';

// If a state should not be reset on logout, check /front/src/redux/store.ts

/**
 * This redux state manages the cache of the messages loaded in a topic page: both the normal page and the yakli page (link opening a message with its context: its parent messages).
 * The messages tree is denormalized.
 * - On one side, we have a dictionary of messages (state.threads.messages). Key: message ID, value: the full message object.
 * - On the other side, each message object has a reference to its parent (parentMessageId and childMessagesIds). It uses IDs to navigate in the tree, to make it easier to update the tree later while keeping everything linked.
 * Don't use parentMessage and childMessages: they are legacy fields that are not reliable anymore.
 * Tip: when a user starts to write a new comment (message), to show an editor in the bubbleflow, we create a temporary new child with a special ID that is rendered slightly differently in the components.
 */

export type MessageId = string;

interface PaginationState {
  page?: number;
  offset?: number;
  limit?: number;
}

interface CountMeta {
  topLevelMessagesCount?: number;
  allMessagesCount?: number;
}

interface AddRootMessagesPayload extends CountMeta {
  messages: Message[] | null;
  displayDepth: number;
  isTree: boolean;
}

interface AddYakliInitialMessagesPayload extends CountMeta {
  websiteWithDiscussion: Website | undefined;
  yakliRootMessage: Message | undefined;
  yakliTargetMessage: Message | undefined;
  yakliPositionLevelCommentInDiscussion: number;
  yakliFirstLoading: boolean;
}

interface AddMessageToParentPayload {
  message: Message | undefined;
  parentMessageId: MessageId | undefined;
}

interface UpdateMessagePayload {
  message: Message | undefined;
  noErrorIfMissing?: boolean;
  /** If the admin deletes the message with all its children. The current message is still displayed with the status "deleted" (content replaced by a message), and children are removed. */
  deleteChildren?: boolean;
  /** You can provide either deleteChildren or updateChildren. If both, deleteChildren has precedence. */
  updateChildren?: boolean;
}

export interface ReduxMessage extends Message {
  childMessagesIds: MessageId[] | null;
  childMessagesCount: number | null;
  parentMessageId: MessageId | null;
  selectedChildId: MessageId | null;
  position: number;
  loadingChildren?: boolean;
}

export interface ThreadState extends CountMeta, PaginationState {
  loadingMessages?: boolean;
  errorMessages?: any;
  messages?: Record<MessageId, ReduxMessage> | undefined;
  rootMessages?: MessageId[];
  topLevelMessagesCount?: number;
  allMessagesCount?: number;
  websiteWithDiscussion?: Website | undefined;
  yakliRootMessage?: MessageId; // The message on top of the tree, separated from the rest by a "load more" if the target is at least the 4th level of depth.
  yakliTargetMessage?: MessageId; // The message targeted by the yakli URL that will have a gray background
  yakliAncestorId?: MessageId;
  yakliPositionLevelCommentInDiscussion?: number;
}

const initialState: ThreadState = {};

// packages/front/src/redux/store.ts
export const threadsSlice = createAppSlice({
  name: 'threads',
  initialState,
  reducers: {
    /** Called when the initial list of messages is loaded in a topic page. Either from a paginated list (sort: oldest or newest) or infinite scroll (sort: relevance). The messages tree is stored in the redux state. addMessagesToTree is the recursive function that reads the tree and extracts all relevant information to store in the state. */
    addRootMessages(state, { payload }: PayloadAction<AddRootMessagesPayload>) {
      const { messages, displayDepth, isTree, ...meta } = payload;
      if (messages) {
        if (!state.messages) state.messages = {};
        addMessagesToTree(messages, state.messages, 0, { displayDepth, isTree });
        state.rootMessages = messages.filter(msg => msg.id).map(msg => msg.id as MessageId);
      }
      Object.assign(state, meta);
      state.loadingMessages = false;
      state.errorMessages = undefined;
    },

    /** Similarly to addRootMessages, this function adds the initial messages when loading a yakli page. A yakli page can have 2 parts when there are lots of parent messages before the target message. The first (root) messages, then a "collapsed" block (clickable to load more intermediate messages), then the messages around the target. When all intermediate messages have been loaded, the root message is hidden and only the below tree remains (which includes the root message, but rendered with the complete tree). */
    addYakliInitialMessages(state, { payload }: PayloadAction<AddYakliInitialMessagesPayload>) {
      // Nomenclature - let's take an example of yakli page with this branch of messages displayed:
      // msg1, ...(collapsed)..., msg4, msg5, msg6 (in gray),
      // msg1 => root message
      // msg4 => ancestor message
      // msg6 => target message

      const { yakliRootMessage, yakliTargetMessage, yakliFirstLoading, ...meta } = payload;
      const { yakliPositionLevelCommentInDiscussion: targetPosition } = meta;
      if (!state.messages) state.messages = {};
      let yakliRootMessage2: ReduxMessage | undefined = undefined;
      if (yakliRootMessage?.id) {
        state.yakliRootMessage = yakliRootMessage.id;
        yakliRootMessage2 = addMessageToTree(yakliRootMessage, state.messages, 0, {
          yakliTargetMessageId: yakliTargetMessage?.id,
        });
      }
      if (yakliTargetMessage?.id) {
        // Cache to help in a special case: link the root message with the first ancestor when climbing up the tree of messages.
        const yakliRootMessageChildren = yakliRootMessage2?.childMessagesIds
          ? new Set(yakliRootMessage2?.childMessagesIds)
          : undefined;

        addMessageToTree(yakliTargetMessage, state.messages, targetPosition, {
          yakliRootMessageChildren,
          yakliRootMessageId: yakliRootMessage2?.id,
          yakliTargetMessageId: yakliTargetMessage.id,
          yakliFirstLoading,
        });
        state.yakliTargetMessage = yakliTargetMessage.id;

        // Now we calculate the ancestor.
        state.yakliAncestorId = getAncestorId(yakliTargetMessage.id, state.messages);
      }
      Object.assign(state, meta);
      state.loadingMessages = false;
      state.errorMessages = undefined;
    },

    clearYakli(state) {
      state.yakliAncestorId = undefined;
      state.yakliPositionLevelCommentInDiscussion = undefined;
      state.yakliRootMessage = undefined;
      state.yakliTargetMessage = undefined;
      state.loadingMessages = undefined;
    },

    /** When fetching collapsed messages, addExtraYakliParentMessages adds the extra loaded messages to the tree. */
    addExtraYakliParentMessages(state, { payload }: PayloadAction<Message | undefined>) {
      const message = payload;
      if (!message) return;
      if (!state.messages) state.messages = {};
      assertMessageExists(state.messages, state.yakliRootMessage!);
      const rootMsg = state.messages[state.yakliRootMessage!];
      if (message?.id) {
        // Cache to help in a special case: link the root message with the first ancestor when climbing up the tree of messages.
        const yakliRootMessageChildren = rootMsg?.childMessagesIds
          ? new Set(rootMsg?.childMessagesIds)
          : undefined;

        const currentPos = state.messages[message.id].position;

        addMessageToTree(message, state.messages, currentPos, {
          yakliRootMessageChildren,
          yakliRootMessageId: state.yakliRootMessage,
          yakliTargetMessageId: state.yakliTargetMessage,
          yakliFirstLoading: true,
        });

        // Now we calculate the ancestor.
        state.yakliAncestorId = getAncestorId(message.id, state.messages);
      }
    },

    /** When a (partial) message updates for any reason (edit, deletion, status update...), this method updates the message object in the store. Thanks to how redux works with selectors, the rest of the app should be updated. Except the code that has not been updated yet to use redux (old code still relying on component states or Apollo cache). */
    updateMessage(state, { payload }: PayloadAction<UpdateMessagePayload>) {
      const { message, noErrorIfMissing, deleteChildren, updateChildren } = payload;

      if (message?.id) {
        const msgId = message.id;
        if (!noErrorIfMissing && !state.messages?.[msgId])
          throw new Error(
            `[bug] The message ${msgId} does not exist yet in the state, it cannot be updated: ${JSON.stringify(
              message,
            )}`,
          );

        if (state.messages?.[msgId]) {
          state.messages[msgId].loadingChildren = false;
          if (!updateChildren) {
            Object.assign(state.messages[msgId], message);
          }
          if (deleteChildren) {
            deleteMessageChildren(state, state.messages[msgId]);
          } else if (updateChildren) {
            const currentPos = state.messages[msgId].position;

            addMessageToTree(message, state.messages, currentPos, {});
          }
        }
        // Do we need to update the childMessagesIds? Is there a scenario when childMessages change here?
      }
    },

    /** Adds a newly created message in the tree. I.e. when a user submits a new comment. */
    addMessageToParent(state, { payload }: PayloadAction<AddMessageToParentPayload>) {
      const { message, parentMessageId = null } = payload;
      if (!message) return;
      if (!state.messages) state.messages = {};
      const messageId = message.id;
      if (!messageId) {
        throw new Error(
          `[bug] cannot add message in store, missing ID: ${JSON.stringify(message)}`,
        );
      }
      const parentMessage = !parentMessageId ? null : state.messages[parentMessageId];
      const reduxMessage: ReduxMessage = {
        ...message,
        parentMessageId,
        childMessagesIds: null,
        childMessagesCount: null,
        selectedChildId: null,
        position: parentMessage?.position || 0,
      };
      state.messages[messageId] = reduxMessage;
      if (parentMessage) {
        if (!parentMessage.childMessagesIds) parentMessage.childMessagesIds = [];
        if (!parentMessage.childMessagesIds.includes(messageId)) {
          parentMessage.childMessagesIds.push(messageId);
          if (parentMessage.childMessagesCount != null) {
            parentMessage.childMessagesCount++;
          }
        }
        parentMessage.selectedChildId = messageId;
      } else {
        if (!state.rootMessages) state.rootMessages = [];
        state.rootMessages.unshift(messageId);
      }
    },

    startLoadingMessages(state) {
      state.loadingMessages = true;
    },
    stopLoadingMessages(state) {
      state.loadingMessages = false;
    },
    loadMessagesError(state, { payload }: PayloadAction<any>) {
      state.loadingMessages = false;
      state.errorMessages = payload;
    },
    updatePagination(state, { payload }: PayloadAction<PaginationState>) {
      Object.assign(state, payload);
    },
    changeMessageInBbf(
      state,
      { payload }: PayloadAction<{ selectedId: MessageId | undefined; parentMessageId: MessageId }>,
    ) {
      const { selectedId, parentMessageId } = payload;

      assertMessageExists(state.messages, parentMessageId);
      const parent = state.messages[parentMessageId];

      parent.selectedChildId = selectedId || null;

      // When opening a node, it should completely open the first branch, i.e. open the first child of all descendant nodes.
      openFirstBranch(state.messages, selectedId);
    },
    openNewResponseChildInBbf(state, { payload }: PayloadAction<MessageId | undefined>) {
      const parentMessageId = payload;

      if (state.messages && parentMessageId && state.messages[parentMessageId]) {
        const parent = state.messages[parentMessageId];
        parent.selectedChildId = newResponseKeyInBbf || null;
      }
    },
    closeEditorCancelAction(
      state,
      { payload }: PayloadAction<{ parentMessageId: MessageId | undefined }>,
    ) {
      // Change bbf selected message. If provided, uses it. Otherwise, fallbacks to the last non-editor message.
      const { parentMessageId } = payload;

      assertMessageExists(state.messages, parentMessageId!);
      const parent = state.messages[parentMessageId!];

      const firstChildId = parent.childMessagesIds?.[0];
      parent.selectedChildId = firstChildId ? firstChildId : null;
    },
    startLoadingMessageChildren(state, { payload }: PayloadAction<MessageId>) {
      const messageId = payload;
      if (messageId !== newResponseKeyInBbf) {
        const message = getStateMessageStrict(
          state.messages,
          messageId,
          'startLoadingMessageChildren',
        );
        message.loadingChildren = true;
      }
    },
    stopLoadingMessageChildren(state, { payload }: PayloadAction<MessageId>) {
      const messageId = payload;
      if (messageId !== newResponseKeyInBbf) {
        const message = getStateMessageStrict(
          state.messages,
          messageId,
          'stopLoadingMessageChildren',
        );
        message.loadingChildren = false;
      }
    },
  },
});

export const {
  addRootMessages,
  addYakliInitialMessages,
  clearYakli,
  addExtraYakliParentMessages,
  updateMessage,
  addMessageToParent,
  startLoadingMessages,
  stopLoadingMessages,
  loadMessagesError,
  updatePagination,
  changeMessageInBbf,
  openNewResponseChildInBbf,
  closeEditorCancelAction,
  startLoadingMessageChildren,
  stopLoadingMessageChildren,
} = threadsSlice.actions;

export const selectMessages = (state: RootState) => state.threads.messages;

export const selectYakliRootMessage = (state: RootState) =>
  !state.threads.yakliRootMessage
    ? undefined
    : state.threads.messages?.[state.threads.yakliRootMessage];
export const selectYakliAncestorMessage = (state: RootState) =>
  !state.threads.yakliAncestorId
    ? undefined
    : state.threads.messages?.[state.threads.yakliAncestorId];
export const selectYakliTargetMessage = (state: RootState) =>
  !state.threads.yakliTargetMessage
    ? undefined
    : state.threads.messages?.[state.threads.yakliTargetMessage];
export const selectYakliTargetMessageId = (state: RootState) =>
  !state.threads.yakliTargetMessage
    ? undefined
    : state.threads.messages?.[state.threads.yakliTargetMessage]?.id;

export function createSelectorChildMessagesIdsExceptEditor(parentMessageId: MessageId | undefined) {
  return createSelector(selectMessages, allMessages => {
    return messagesStateToChildMessagesIds(allMessages, parentMessageId, true);
  });
}

export function messagesStateToChildMessagesIds(
  allMessages: ThreadState['messages'],
  parentMessageId: MessageId | undefined,
  skipNewMessage?: boolean,
) {
  const parent = allMessages?.[parentMessageId || ''];
  const isNewResponse = parent?.selectedChildId === newResponseKeyInBbf;
  return (
    (!parent?.childMessagesIds
      ? parent?.childMessagesIds
      : !isNewResponse || skipNewMessage
      ? parent.childMessagesIds
      : [...parent.childMessagesIds, newResponseKeyInBbf]) || []
  );
}
